import { toLuxon } from "./shared/dateTimeUtils";
import service from "./service";
import { MODULE_ID } from "./shared/moduleIds";
import config from "./config";
import pkg from "../package.json";

export const getDocuments = (asmpid) =>
  service("emr/DocumentSummary").get({ asmpid });
export const getDocument = ({ documentId, parentId, asmpid }) =>
  service("emr/Document").get({
    documentId,
    parentId,
    asmpid,
  });
export const getLabs = ({ asmpid, type }) =>
  service("labs").get({ asmpid, type });
export const getLabOrder = ({ asmpid, orderId, eventId, hasCorrections }) =>
  service("emr/LaboratoryOrder").get({
    asmpid,
    orderId,
    eventId,
    hasCorrections,
  });
export const getMeds = (asmpid) => service("emr/medications").get({ asmpid });
export const getVitals = (asmpid) =>
  service("emr/vitals").get({
    asmpid,
    moduleId: MODULE_ID.ELECTRONIC_MEDICAL_RECORD,
  });
export const getPatient = ({ asmpid, moduleId }) =>
  service("patient").get({
    moduleId,
    asmpid,
  });
export const getPatientConfig = (params) =>
  service("patientlist/configuration").get(params);
export const getSearchConfig = ({ asmpid, moduleId }) =>
  service("configuration/searchfields").get({ asmpid, moduleId });
export const getPatients = (parameters, options) => {
  console.assert(!isNaN(Number(parameters?.moduleId)), "no module-id set");
  return service("patientlist", options).get(parameters);
};
export const getPatientSummary = ({ asmpid, summaryType, moduleId }) =>
  service("patient/summary").get({ asmpid, summaryType, moduleId });

export const getObPatientSummary = ({ adusername, asmpid, fromdate }) =>
  service("ob/obsummary").get({ adusername, asmpid, fromdate });
export const getVentConfig = (asmpid) => service("vent/config").get({ asmpid });
export const getDiscretes = (asmpid, options) =>
  service("vent/discretes", options).get({ asmpid });
export const getVentSettings = (asmpid) =>
  service("vent/settings").get({ asmpid });
export const getVentTrends = ({ asmpid, keys }) =>
  service("vent/trends").get({ asmpid, keys });
export const getVentEvents = (asmpid) => service("vent/events").get({ asmpid });

export const postLogin = ({
  username,
  password,
  clientHeaders,
  moduleId,
  quiet,
}) =>
  service("logon", { quiet }).post({
    model: { username, password, moduleId },
    clientHeaders: clientHeaders,
  });
export const fetchLoginStatus = () => service("logon/status").get();

export const getMonitorConfig = (asmpid) =>
  service("pm/config").get({ asmpid });
export const getMonitorEvents = (query) => service("pm/events").get(query);

// use seconds for most recent
// starttime and endtime for selections
// bookmark for streaming.
export const getMonitorMeasurements = (
  {
    asmpid,
    bookmark,
    seconds,
    starttime,
    endtime,
    keys,
    discretekeys,
    downSample,
  },
  options = {},
) =>
  service("pm/measurements", { quiet: true, ...options }).get({
    asmpid,
    bookmark,
    seconds,
    keys,
    discretekeys,
    downSample,
    starttime:
      starttime &&
      toLuxon(starttime)
        .toUTC()
        .set({ millisecond: 0 })
        .toISO({ suppressMilliseconds: true }),
    endtime:
      endtime &&
      toLuxon(endtime)
        .toUTC()
        .set({ millisecond: 0 })
        .toISO({ suppressMilliseconds: true }),
  });

export const getMultiMeasurements = ({
  asmpids,
  bookmark,
  seconds,
  starttime,
  endtime,
  keys,
  discretekeys,
  downSample,
  controller,
  quiet = true,
}) =>
  service("pm/multiMeasurements", { quiet: quiet, controller }).get({
    asmpid: asmpids,
    bookmark,
    seconds,
    keys,
    discretekeys,
    downSample,
    starttime: starttime?.replace(/\.\d+/, ""),
    endtime: endtime?.replace(/\.\d+/, ""),
  });
export const getMonitorTrends = ({ asmpid, keys, starttime }, options = {}) =>
  service("pm/trends", { ...options })
    .get({ asmpid, keys, starttime })
    .then((model) => {
      return {
        trends: (model?.data || []).map((trend) => ({
          ...trend,
          startTime: model.startTime,
          values: trend.values.map((value, i) => ({
            value,
            offset: i * model.interval,
          })),
        })),
      };
    });
export const postQrsProcessor = ({ query, model }) =>
  service("services/acwa/post/qrsprocessor").post({ query, model });
export const getAcwaVersion = () =>
  service("services/acwa/version", { unwrapped: true }).get();

const snippetsRootPath = "aso/post/snippets/";

// query = {asmpid}
export const getSnippetPreview = ({ query, model }) =>
  service(`${snippetsRootPath}previewsnippet/`).post({ query, model });
export const fetchJpgSnippetPreview = ({ query, model }) =>
  service(`${snippetsRootPath}previewwebsnippet/`).post({ query, model });

export const validateSnippetParameters = ({ asmpid, mrn }) =>
  service("aso/snippets/ValidateSnippetParameters").get({ asmpid, mrn });
export const approveSnippet = ({ id }) =>
  service(`${snippetsRootPath}${id}/approve`).post({});
export const rejectSnippet = ({ id }) =>
  service(`${snippetsRootPath}${id}/reject`).post({});

// query = {asmpid, worklistId}
export const savePMSnippet = ({ query, model }) =>
  service(`${snippetsRootPath}savesnippet/`).post({ query, model });
export const getSnippetEventDescriptionLibrary = () =>
  service("aso/snippeteventdescriptionlibrary/get").get();

export const postOpsLog = ({
  operationType = "",
  extraContext = {},
  context = window?.location?.href,
}) =>
  service(`aso/post/operationlog/logoperation`, { quiet: true }).post({
    model: {
      type: `${pkg.name} v${pkg.version}`,
      operationType,
      context,
      extraContext,
    },
  });

export const getECGList = (asmpid) => service("cardio/ecglist").get({ asmpid });
export const getECG = ({ asmpid, ecgId, ecg2id }) =>
  service("cardio/ecg").get({ asmpid, ecgId, ecg2id });
export const getECGStatementLibrary = (asmpid) =>
  service("cardio/statementlibrary").get({ asmpid });

export const fetchEcgEdit = (ecgId) =>
  service("cardio/requestedit").get({ ecgId });
export const postEcgEdit = ({ ecgId, statements }) =>
  service("cardio/post/ecg/confirm").post({
    query: { ecgId },
    model: statements,
  });

// export const saveECGStatements = ({ecg, ecgTimestamp}) => service('cardio/post/ecg/confirm').get({ecg, ecgTimestamp});
// export const acceptCardioAlert = (notificationId) => service('cardio/alert/accept').get({notificationId});

export const fetchVersion = () => service("version", { quiet: true }).get();

export const fetchPrefs = () =>
  service(`aso/configuration/userconfiguration`, { quiet: true }).get();
export const savePrefs = (pref) =>
  service(`aso/post/configuration/userconfiguration`, { quiet: true }).post({
    model: pref,
  });

export const decryptShareLink = (model) =>
  service("c3p0/decrypt").post({ model });

// conversation
export const getContacts = () => service("aso/contacts/getbysiteid").get();
export const getConversationDetail = ({
  conversationId,
  fromMessageId,
  count,
}) =>
  service("aso/messaging/conversation").get({
    conversationId,
    fromMessageId,
    count,
  });
export const getConversationInfo = ({ recipientContactId }) =>
  service("aso/messaging/conversationid").get({ recipientContactId });
export const getConversationList = () =>
  service("aso/messaging/conversations").get();
export const deleteConversation = ({ conversationId }) =>
  service("aso/messaging/conversation/delete").get({ conversationId });
export const doSendMessage = (requestPayload) =>
  service("aso/post/messaging/sendmessage").post({ model: requestPayload });

export const fetchSnippetReports = ({
  fromDate,
  toDate,
  units,
  facilityCodes,
  includePurgePending,
  bedListId,
} = {}) =>
  service("aso/document/nursereport").get({
    fromDate,
    toDate,
    units,
    facilityCodes,
    includePurgePending,
    bedListId,
  });
export const fetchSnippetUnits = () =>
  service("aso/configuration/siteunits").get();
export const fetchReportLocations = () =>
  service("aso/document/reportlocations").get();
export const fetchSnippetReportBedLists = () =>
  service(`aso/lists/${config.siteid}`, { quiet: true }).get();

export const fetchSnippetReport = ({ documentid, asmpid }) =>
  service("aso/document/Document").get({ documentid, asmpid });
export const fetchSnippetTypes = () =>
  service("aso/document/documentlabels", { quiet: true }).get();
export const fetchObWaveform = service("ob/waveform").get;
export const fetchObVitals = ({ adusername, asmpid, moduleId }, options) =>
  service("ob/vitals", options).get({ adusername, asmpid, moduleId });

export const getTeleDiscontinuationReport = (patients) => {
  return service("aso/post/tele/generatediscontinuationreport").post({
    model: patients,
  });
};
export const getTeleCandidates = ({
  fromDate,
  toDate,
  units,
  facilityCodes,
  includePurgePending,
  bedListId,
} = {}) =>
  service("aso/document/telediscontinuationreport").get({
    fromDate,
    toDate,
    units,
    facilityCodes,
    includePurgePending,
    bedListId,
  });

export const getAhiSystemUrl = () => service("ahi-system-url").get();
export const getAhiDescriptions = () =>
  service("services/ahi/resource/alltext").get();
export const getPatientAhiData = (asmpid) =>
  service(`services/ahi/AnalysisResult/${asmpid}`, { quiet: true }).get();
export const getAllPatientAhiData = (asmpids) =>
  service(`services/ahi/AnalysisResult/PostPatientList`, { quiet: true }).post({
    model: asmpids,
  });

export const getSites = () => service(`localservices/sitelist`).get();

export const postSitesLogin = ({
  username,
  password,
  clientHeaders,
  quiet = true,
}) =>
  service("localservices/login", { quiet }).post({
    model: { username, password },
    clientHeaders: clientHeaders,
  });

export const postSitesLogoff = ({ clientHeaders, quiet = true }) =>
  service("localservices/logoff", { quiet }).post({
    clientHeaders: clientHeaders,
  });
